import './styles.scss';

import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import dayjs, { Dayjs } from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  useLazyDownloadFreeReportQuery,
  useLazyDownloadTaskReportQuery,
} from '@/api/report/api';
import { freeColumns } from '@/pages/Report/columns/freeColumns';
import { taskColumns } from '@/pages/Report/columns/taskColumns';
import { freeReportLevel } from '@/pages/Report/routes.config';
import {
  FreeReport,
  FreeReportFilter,
  TaskReport,
  TaskReportFilter,
} from '@/pages/Report/types';
import { Sort, TFilters } from '@/shared/api/types';
import { useAppNotification } from '@/shared/hooks/useAppNotification';
import { ReportTitles, ReportType } from '@/shared/report/enum';
import { report } from '@/shared/security/permissions/report';
import { downloadBlob, mapTableIndex } from '@/utils/utils';

import { extraButtonConfig } from './buttons.config';

const columnConfig = {
  [ReportType.TASK]: taskColumns,
  [ReportType.FREE]: freeColumns,
};

interface ReportTableProps {
  type: ReportType;
  dateAt: Dayjs | [Dayjs, Dayjs];
  data?: TaskReport[] | FreeReport[];
  filters: TFilters;
  onChange?: (sort?: Sort[]) => void;
  currentSort?: Sort[];
}

const formatDate = (type: ReportType, dateAt: Dayjs | [Dayjs, Dayjs]) => {
  if (!dateAt) return '';

  if (type === ReportType.TASK) {
    const [from, to] = dateAt as [Dayjs, Dayjs];
    return `${dayjs(from).format('DD.MM.YYYY')}-${dayjs(to).format('DD.MM.YYYY')}`;
  }
  return dayjs(dateAt as Dayjs).format('DD.MM.YYYY');
};

export const ReportTable = memo(
  ({ type, dateAt, data, filters, onChange, currentSort }: ReportTableProps) => {
    const [searchParams] = useSearchParams();
    const formattedDate = formatDate(type, dateAt);
    const orgName = searchParams.get('orgName') || '';
    const { showWarning } = useAppNotification();
    const [triggerTaskDownload, orderDownloadResult] = useLazyDownloadTaskReportQuery();
    const [triggerFreeDownload, freeDownloadResult] = useLazyDownloadFreeReportQuery();

    const handleDownload = useCallback(async () => {
      if (!dateAt || !filters) return;

      let result;
      if (type === ReportType.TASK) {
        result = await triggerTaskDownload(
          { filters: filters as TaskReportFilter },
          false,
        ).unwrap();
      } else {
        result = await triggerFreeDownload(
          { filters: filters as FreeReportFilter },
          false,
        ).unwrap();
      }

      if (result.meta.response?.status === 204) {
        showWarning('Внимание!', 'Отсутствуют данные для формирования файла');
        return;
      }

      const filename = `${ReportTitles[type]} ${orgName} ${formattedDate}.xlsx`;
      downloadBlob(result.data, filename);
    }, [
      type,
      dateAt,
      formattedDate,
      filters,
      triggerTaskDownload,
      triggerFreeDownload,
      orgName,
    ]);

    const columns = useMemo(() => {
      if (type === ReportType.FREE) {
        return columnConfig[type](dayjs(dateAt as Dayjs).format('YYYY-MM-DD'));
      }
      return columnConfig[type];
    }, [type, dateAt]);

    const tableData = useMemo(() => {
      if (!data) return [];

      if (type === ReportType.TASK) {
        return mapTableIndex<TaskReport>(data as TaskReport[]);
      } else {
        return mapTableIndex<FreeReport>(data as FreeReport[]);
      }
    }, [data, type]);

    const permitsConfig = useMemo(() => {
      return type === ReportType.TASK
        ? { permissions: report.taskReport.export }
        : {
            permissions: report.summaryReport.export,
            level: freeReportLevel,
          };
    }, [type]);

    return (
      <>
        <PageTitle
          title={
            `${ReportTitles[type]} ${orgName}` +
            (type === ReportType.FREE ? `за ${formattedDate}` : '')
          }
        />
        <UTable
          size={'small'}
          data={tableData}
          columns={columns}
          currentSort={currentSort}
          onChange={onChange}
          buttons={extraButtonConfig({
            onDownload: handleDownload,
            isDownloading:
              type === ReportType.TASK
                ? orderDownloadResult.isFetching
                : freeDownloadResult.isFetching,
            security: permitsConfig,
            disabled: !tableData.length,
          })}
        />
      </>
    );
  },
);
